import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Base notification creator
export const createNotification = async ({ userId, type, message, defectId, defectTitle }) => {
  try {
    await addDoc(collection(db, 'notifications'), {
      userId,
      type,
      message,
      defectId: defectId || null,
      defectTitle: defectTitle || null,
      read: false,
      dismissed: false,
      createdAt: serverTimestamp()
    });
  } catch (err) {
    console.error('Error creating notification:', err);
  }
};

// Batch notify multiple users
export const notifyMultipleUsers = async (userIds, { type, message, defectId, defectTitle }) => {
  const promises = userIds.map(userId => 
    createNotification({ userId, type, message, defectId, defectTitle })
  );
  
  try {
    await Promise.all(promises);
  } catch (err) {
    console.error('Error sending batch notifications:', err);
  }
};

// Notify assigned users (Fix + Test)
export const notifyAssignedUsers = async (defect, users, message, type, currentUserId) => {
  const assignedEmails = [defect.assignedToFix, defect.assignedToTest].filter(Boolean);
  
  for (const email of assignedEmails) {
    const user = users.find(u => u.email === email);
    if (user && user.id !== currentUserId) {
      await createNotification({
        userId: user.id,
        type,
        message: `${message} "${defect.title}"`,
        defectId: defect.id,
        defectTitle: defect.title
      });
    }
  }
};

// Notify defect creator
export const notifyDefectCreator = async (defect, currentUserId, message, type = 'defect_updated') => {
  if (defect.createdBy && defect.createdBy !== currentUserId) {
    await createNotification({
      userId: defect.createdBy,
      type,
      message: `${message} "${defect.title}"`,
      defectId: defect.id,
      defectTitle: defect.title
    });
  }
};

// Notify on new assignment
export const notifyOnAssignment = async (users, email, defectId, defectTitle, assignmentType) => {
  if (!email) return;
  
  const user = users.find(u => u.email === email);
  if (!user) return;

  const type = assignmentType === 'fix' ? 'assigned_fix' : 'assigned_test';
  const action = assignmentType === 'fix' ? 'fix' : 'test';
  
  await createNotification({
    userId: user.id,
    type,
    message: `You were assigned to ${action} defect "${defectTitle}"`,
    defectId,
    defectTitle
  });
};

// Notify on status change to Resolved
export const notifyStatusResolved = async (defect) => {
  if (defect.createdBy) {
    await createNotification({
      userId: defect.createdBy,
      type: 'status_resolved',
      message: `Defect "${defect.title}" was marked as Resolved`,
      defectId: defect.id,
      defectTitle: defect.title
    });
  }
};

// Notify on defect cancellation
export const notifyDefectCancelled = async (defect, users, currentUserId) => {
  // Notify assigned users
  await notifyAssignedUsers(defect, users, 'Defect was cancelled', 'defect_cancelled', currentUserId);
  
  // Notify creator
  await notifyDefectCreator(defect, currentUserId, 'Your defect was cancelled', 'defect_cancelled');
};

// Notify on comment
export const notifyOnComment = async (defect, users, commenterEmail, currentUserId) => {
  // Notify creator
  if (defect.createdBy !== currentUserId) {
    await createNotification({
      userId: defect.createdBy,
      type: 'comment_added',
      message: `${commenterEmail} commented on your defect "${defect.title}"`,
      defectId: defect.id,
      defectTitle: defect.title
    });
  }

  // Notify assigned users
  const assignedEmails = [defect.assignedToFix, defect.assignedToTest].filter(Boolean);
  
  for (const email of assignedEmails) {
    const user = users.find(u => u.email === email);
    if (user && user.id !== currentUserId) {
      await createNotification({
        userId: user.id,
        type: 'comment_added',
        message: `${commenterEmail} commented on defect "${defect.title}"`,
        defectId: defect.id,
        defectTitle: defect.title
      });
    }
  }
};

// Notify all admins (e.g., new account created)
export const notifyAllAdmins = async (users, message, type = 'new_account') => {
  const adminUsers = users.filter(u => u.role === 'Admin');
  
  for (const admin of adminUsers) {
    await createNotification({
      userId: admin.id,
      type,
      message,
      defectId: null,
      defectTitle: null
    });
  }
};
